// <purpose>THE third slot of every bottom bar (bottom-bar decision, 2026-10-16): „Nicht hilfreich"
// and its chooser, one piece both bars mount — the letter flow's (TicketDetail) and the triage
// row's (both through PressBar) — so the verb, its faces, its reasons and the press itself cannot drift
// between roles. The press IS the chooser: pressing the button raises a small list above the slot
// („Was trifft stattdessen zu?": the tenant's served axis words, then „Von Hand beantwortet",
// „Kein Vorgang", and last „Ohne Angabe ablegen"); picking a row is the whole act — one call, the
// "instead" inside or null. The receiver only presses: nothing here decides what the act does to
// the row (core files it, in one transaction).</purpose>
// <constraints>Layout law: the chooser is a FIXED layer above the slot (a portal, measured off the
// button's rect the way InfoTooltip places itself), so raising it moves nothing in the bar or the
// pane — and nothing clips it: the bottom bar sits in a box that scrolls, and a layer positioned
// inside that box was measured cut off on the first live walk (2026-10-16). The slot is always mounted on every row state — open: filled and
// pressable; settled (approved, archived, already declined): outlined, with the reason in its
// title — exactly the idiom the draft slots use. Keyboard: digits pick a row, Enter takes „Ohne
// Angabe ablegen", Esc closes with nothing filed. German strings here are the act's own verbs,
// code-owned like Genehmigen and Neu analysieren; the axis words are served (cm.label).</constraints>
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ActionButton, CrossIcon } from './shared';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import type { DeclineInstead } from '../types';
import { DECLINE_LABEL, declineChooserRows, declineSlotFace } from '../services/declineVerdict';
import type { DeclineChooserRow } from '../services/declineVerdict';
export { DECLINE_LABEL, DECLINE_REASON, CHOOSER, declineSlotFace, declineChooserRows, declineInsteadWord } from '../services/declineVerdict';
export type { DeclineSlotFace, DeclineChooserRow } from '../services/declineVerdict';

export interface DeclineSlotProps {
  status: string;
  previousStatus?: string | null;
  primaryAxis?: string | null;
  /** The row offers no draft at all — „Kein Vorgang" leads the chooser. */
  noDraft: boolean;
  /** The host's own writes are on the wire or paused — the slot refuses without changing face. */
  writesBlocked: boolean;
  maintenance: boolean;
  /** The press. The host owns the mutation and its post-effects; the slot closes its chooser at once. */
  onDecline: (instead: DeclineInstead | null) => void | Promise<void>;
  /** Weight beside a recommended act — a weight, never a state. */
  emphasis?: 'primary' | 'secondary';
  /** Fill the bar's third (PressBar) instead of sizing to the label. */
  fill?: boolean;
  /** A counter the host bumps when the N key asks for the chooser (the press keys, 2026-10-28). */
  openRequest?: number;
}

export const DeclineSlot: React.FC<DeclineSlotProps> = ({
  status, previousStatus, primaryAxis, noDraft, writesBlocked, maintenance, onDecline, emphasis = 'primary',
  fill, openRequest,
}) => {
  const cm = useCategoryMeta();
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  // Measuring keeps the OLD rect when nothing moved: a fresh DOMRect per measure re-rendered, the
  // re-render rebuilt the key handler, the handler re-ran this effect, the effect measured again —
  // a loop that hung the suite for two hours (2026-10-16) and would have spun the browser.
  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const next = el.getBoundingClientRect();
    setRect(prev => (prev && prev.top === next.top && prev.left === next.left ? prev : next));
  }, []);
  const { face, title } = declineSlotFace(status, previousStatus, maintenance);
  const { heading, listed, enterRow } = useMemo(() => {
    const { heading, rows } = declineChooserRows(cm, { primaryAxis, noDraft });
    // The row that Enter takes — always the last one („Ohne Angabe ablegen").
    return { heading, listed: rows.slice(0, -1), enterRow: rows[rows.length - 1] };
  }, [cm, primaryAxis, noDraft]);

  const pick = useCallback((row: DeclineChooserRow) => {
    setOpen(false);
    void onDecline(row.instead);
  }, [onDecline]);

  // The keys are taken at the DOCUMENT, capture phase, while the chooser is up: Esc, Enter, the
  // digits, H and K act on the chooser whatever else holds focus (the first live walk lost an Enter
  // to a first-run tooltip's button). The layer still takes focus for the screen reader.
  const onKey = useCallback((e: KeyboardEvent) => {
    const settle = () => { e.preventDefault(); e.stopPropagation(); };
    if (e.key === 'Escape') { settle(); setOpen(false); return; }
    if (e.key === 'Enter') { settle(); pick(enterRow); return; }
    // Digits, H and K pick by the row's own hotkey (declineChooserRows), never by position.
    const row = listed.find(r => r.hotkey !== null && r.hotkey.toLowerCase() === e.key.toLowerCase());
    if (row) { settle(); pick(row); }
  }, [listed, enterRow, pick]);

  // Opening: measure once, take focus, and watch the mouse and the viewport. Keyed on `open` alone,
  // so a re-render while the chooser stands never measures again.
  useEffect(() => {
    if (!open) return;
    measure();
    dialogRef.current?.focus();
    // A click anywhere outside the slot AND the layer closes it with nothing filed (Esc's mouse twin).
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t) || dialogRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open, measure]);

  // The keys follow their handler separately — swapping the handler must not re-measure.
  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onKey]);

  const disabled = face !== 'on' || writesBlocked;
  // The N key: open the chooser exactly as a press would, never on mount.
  const lastRequest = useRef(openRequest);
  useEffect(() => {
    if (openRequest === lastRequest.current) return;
    lastRequest.current = openRequest;
    if (!disabled) setOpen(true);
  }, [openRequest, disabled]);
  return (
    // flex '1 0 auto': stretches to a reserved slot (the triage bar) but never shrinks below its
    // own label — the first live walk showed the label wrapping onto two lines in the letter bar.
    <span ref={wrapRef} data-testid="decline-slot" style={fill ? { display: 'flex', width: '100%', minWidth: 0 } : { display: 'inline-flex', flex: '1 0 auto' }}>
      <ActionButton
        fill={fill}
        label={DECLINE_LABEL}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
        title={title}
        face={face}
        emphasis={emphasis}
        // The cross belongs to THIS slot (operator, 2026-10-16) — on every face, recoloured to its ground.
        icon={<CrossIcon size={14} color={face === 'off' || emphasis === 'secondary' ? theme.colors.navy : theme.colors.white} />}
      />
      {open && rect && createPortal(
        <div
          ref={dialogRef}
          role="menu"
          aria-label={heading}
          tabIndex={-1}
          data-testid="decline-chooser"
          style={{
            position: 'fixed',
            bottom: Math.max(8, window.innerHeight - rect.top + 8),
            left: Math.max(8, Math.min(rect.left, window.innerWidth - 368)),
            minWidth: 280, maxWidth: 360,
            // A tenant with many axis values (VNB serves eleven) scrolls inside the chooser rather
            // than growing past the top of the pane.
            maxHeight: 'min(60vh, 420px)', overflowY: 'auto',
            backgroundColor: theme.colors.white,
            border: `1.5px solid ${theme.colors.navy}`,
            borderRadius: theme.borderRadius.md,
            boxShadow: '0 8px 24px rgba(0,13,102,0.18)',
            padding: `${theme.spacing.sm}px 0`,
            // Above compare mode's stage (900), under the modal layer (1000): the bar works inside it too.
            zIndex: 950,
            animation: 'stx-fadeIn 0.15s ease',
            outline: 'none',
            fontFamily: theme.font.family,
          }}
        >
          <div style={{ padding: `2px ${theme.spacing.md}px ${theme.spacing.sm}px`, fontSize: 12, fontWeight: 600, color: theme.colors.navyMid }}>
            {heading}
          </div>
          {listed.map(row => (
            <ChooserRow key={row.key} hint={row.hotkey} label={row.label} onPick={() => pick(row)} />
          ))}
          <div style={{ borderTop: `1px solid ${theme.colors.borderLight}`, marginTop: 4, paddingTop: 4 }}>
            <ChooserRow key={enterRow.key} hint={enterRow.hotkey} label={enterRow.label} onPick={() => pick(enterRow)} quiet />
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
};

// The hint is the row's hotkey in plain text (no symbol glyphs — the product emits none); a row
// without one keeps the hint's box so the labels stay aligned.
const ChooserRow: React.FC<{ hint: string | null; label: string; onPick: () => void; quiet?: boolean }> = ({ hint, label, onPick, quiet }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onPick}
    style={{
      display: 'flex', alignItems: 'center', gap: theme.spacing.sm, width: '100%',
      padding: `6px ${theme.spacing.md}px`, border: 'none', background: 'transparent',
      cursor: 'pointer', textAlign: 'left', fontFamily: theme.font.family, fontSize: 13,
      color: quiet ? theme.colors.navyMuted : theme.colors.navy, fontWeight: quiet ? 400 : 500,
    }}
    onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.colors.bgLight; }}
    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
  >
    <span aria-hidden="true" style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 18, height: 18, padding: '0 4px', borderRadius: 4, fontSize: 10.5, fontWeight: 600,
      color: theme.colors.navyMid, backgroundColor: hint ? theme.colors.bgLight : 'transparent', flexShrink: 0,
    }}>{hint ?? ''}</span>
    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </button>
);

export default DeclineSlot;
